@if (isDpia()) {
  <!-- DPIA branch — the record uses ICO 3-point overall risk (LOW /
       MEDIUM / HIGH per Veritise DPIA spec §7), not the AI-Risk
       GDPR / Cyber / AI-Act triplet. Renders `Overall · X` plus an
       optional `Residual · Y` chip; a small `⚠ Art. 36` pill appears
       when residual HIGH remains AND the DPIA has flagged prior
       consultation as required (spec §7 Step 7). -->
  @if (dpiaChips(); as pair) {
    @if (mode() === 'card') {
      <div class="risk-score-row">
        <app-risk-meter
          [scale]="pair.overall.scale"
          [level]="pair.overall.level"
          [tone]="pair.overall.tone"
          [label]="pair.overall.label"
          [prefix]="pair.overall.prefix"
          size="sm"
        />
        @if (pair.residual; as res) {
          <app-risk-meter
            [scale]="res.scale"
            [level]="res.level"
            [tone]="res.tone"
            [label]="res.label"
            [prefix]="res.prefix"
            size="sm"
          />
        }
        @if (showArt36Pill()) {
          <span class="art36-pill" [matTooltip]="art36PillTooltip()" matTooltipPosition="above">
            <mat-icon aria-hidden="true">report_problem</mat-icon>
            {{ art36PillLabel() }}
          </span>
        }
      </div>
    } @else {
      <!-- Inline mode (list-view Risk column): overall as the single
           lede chip; residual + Art.36 stay in the tooltip / detail
           panel to keep the row height fixed. -->
      <div
        class="risk-meter-inline"
        [matTooltip]="pair.residual ? pair.residual.prefix + ' · ' + pair.residual.label : ''"
        matTooltipPosition="left"
      >
        <app-risk-meter
          [scale]="pair.overall.scale"
          [level]="pair.overall.level"
          [tone]="pair.overall.tone"
          [label]="pair.overall.label"
          [prefix]="pair.overall.prefix"
          size="sm"
        />
        @if (showArt36Pill()) {
          <span class="risk-meter-more">Art. 36</span>
        }
      </div>
    }
  } @else if (resolvedEmptyText()) {
    @if (mode() === 'card') {
      <app-empty-state
        variant="inline"
        icon="bar_chart"
        [title]="resolvedEmptyText()"
        [message]="resolvedEmptyHint() || ''"
      />
    } @else {
      <span class="data-table-muted">{{ resolvedEmptyText() }}</span>
    }
  }
} @else if (scores(); as chips) {
  @if (mode() === 'card') {
    <!-- Card mode — one row of meter chips, each carrying its own
         framework prefix inside the chip ("GDPR · High"). The prior
         uppercase column-label + bare-chip stack duplicated what the
         chip's own `prefix` already expresses and truncated at typical
         card widths. One row, one label per chip. No stripe background,
         no dividers — the chips speak for themselves. -->
    <div class="risk-score-row">
      @for (chip of chips; track chip.columnLabel) {
        <app-risk-meter
          [scale]="chip.scale"
          [level]="chip.level"
          [tone]="chip.tone"
          [label]="chip.label"
          [prefix]="chip.prefix"
          size="sm"
        />
      }
    </div>
  } @else if (worstChip(); as worst) {
    <!-- Inline mode — narrow table cell. ONE chip: the worst-severity
         dimension (decision-relevant). A `+N` suffix signals the other
         dimensions exist; the tooltip lists them all on hover. Stays a
         single line regardless of how many axes a given assessment
         type tracks — works for future types whose risk levels are
         named differently from the built-in GDPR / Cyber / AI Act. -->
    <div
      class="risk-meter-inline"
      [matTooltip]="breakdownTooltip()"
      matTooltipClass="risk-breakdown-tip"
      matTooltipPosition="left"
    >
      <app-risk-meter
        [scale]="worst.scale"
        [level]="worst.level"
        [tone]="worst.tone"
        [label]="worst.label"
        [prefix]="worst.prefix"
        size="sm"
      />
      @if (extraDimensionCount() > 0) {
        <span class="risk-meter-more">+{{ extraDimensionCount() }}</span>
      }
    </div>
  }
} @else if (resolvedEmptyText()) {
  @if (mode() === 'card') {
    <!-- Canonical inline empty — sized to match the populated 3-column
         stat strip's footprint (~80 px). Same halo'd-icon aesthetic
         as every other empty state in the app; the `inline` variant
         is the dedicated horizontal layout for in-card stat-strip
         contexts. -->
    <app-empty-state
      variant="inline"
      icon="bar_chart"
      [title]="resolvedEmptyText()"
      [message]="resolvedEmptyHint() || ''"
    />
  } @else {
    <!-- Inline mode — muted single-line text, matching the
         `data-table-muted` convention every other table cell uses for
         missing values. -->
    <span class="data-table-muted">{{ resolvedEmptyText() }}</span>
  }
}
